import { OverFlowIcon } from 'assests/GmsIcons';
import React, { useState, useEffect, useRef } from 'react';
import { styles } from '../../../utils/styles';

type GroupConfigType = {
    id: string;
    name: string;
    showTitle: boolean;
};

type GroupConfigProps = {
    id?: string;
    initialConfig?: Partial<GroupConfigType>;
    onConfigChange?: (config: GroupConfigType) => void;
}

export const GroupConfig = ({ id, initialConfig, onConfigChange }: GroupConfigProps) => {
    const baseId = id || 'group-config';
    const nameDebounceRef = useRef<NodeJS.Timeout>();

    const defaultConfig: GroupConfigType = {
        id: 'group-1',
        name: 'Group 1',
        showTitle: true
    };

    const [config, setConfig] = useState<GroupConfigType>({
        ...defaultConfig,
        ...initialConfig
    });

    const [localName, setLocalName] = useState<string>(config.name);

    useEffect(() => {
        if (localName !== config.name) {
            setLocalName(config.name);
        }
    }, [config.name]);

    useEffect(() => {
        if (initialConfig && initialConfig.id) {
            setConfig(prev => ({
                ...defaultConfig,
                ...initialConfig
            }));
        }
    }, [initialConfig]);

    const updateConfig = (updates: Partial<GroupConfigType>) => {
        setConfig(prevConfig => {
            const newConfig = { ...prevConfig, ...updates };
            if (onConfigChange) {
                onConfigChange(newConfig);
            }
            return newConfig;
        });
    };


    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value;
        setLocalName(newName);
        
        if (nameDebounceRef.current) {
            clearTimeout(nameDebounceRef.current);
        }
        
        nameDebounceRef.current = setTimeout(() => {
            updateConfig({ name: newName });
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (nameDebounceRef.current) {
                clearTimeout(nameDebounceRef.current);
            }
        };
    }, []);

    return (
        <div id={baseId} data-testid={baseId} className={styles.container}>
            <div id={`${baseId}-header`} className={`${styles.sectionTitle} tw-flex tw-justify-between tw-items-center`}>
                <h2 id={`${baseId}-title`} className="tw-text-sm tw-text-[#6D788C] tw-font-semibold">Group</h2>
                <button id={`${baseId}-menu-btn`} data-testid={`${baseId}-menu-btn`} className="tw-p-1 tw-text-gray-500">
                    <OverFlowIcon />
                </button>
            </div>

            <div id={`${baseId}-name-section`} className={styles.section}>
                <h3 id={`${baseId}-name-label`} className={styles.subSectionTitle}>Group Name</h3>
                <input
                    id={`${baseId}-name-input`}
                    data-testid={`${baseId}-name-input`}
                    type="text"
                    value={localName}
                    onChange={handleNameChange}
                    className={styles.input}
                    placeholder="Enter group name"
                />
            </div>

            <div id={`${baseId}-show-title-section`} className={styles.section}>
                <label id={`${baseId}-show-title-label`} className="tw-flex tw-items-center">
                    <input
                        id={`${baseId}-show-title-checkbox`}
                        data-testid={`${baseId}-show-title-checkbox`}
                        type="checkbox"
                        checked={config.showTitle}
                        onChange={(e) => {
                            updateConfig({ showTitle: e.target.checked })
                        }}
                        className={styles.checkboxInput}
                    />
                    <span id={`${baseId}-show-title-text`} className={styles.checkboxLabel}>Show Title</span>
                </label>
            </div>
        </div>
    );
};